'use client'

import Image from 'next/image'
import Link from 'next/link'

import { GrPowerReset } from 'react-icons/gr'
import { RiQuestionLine } from 'react-icons/ri'

import { cn } from '../../utils'
import { GENERAL_ENQUIRIES_URL } from '../constants/url'

import { BugReportButton } from './BugReportButton'
import { Route } from './NavBar/NavBar'

type Social = {
  alt: string
  href: string
  icon: string
}

export const Footer = ({
  className,
  excludedRoutes,
  handleReset,
  maxWidth,
  routes,
  showBugReport,
  showGeneralEnquiries,
  socials,
}: {
  routes: Route[]
  socials: Social[]
  excludedRoutes?: string[]
  className?: string
  maxWidth?: string
  handleReset?: () => void
  showBugReport?: boolean
  showGeneralEnquiries?: boolean
}) => {
  const flattenedRoutes = routes
    .flatMap(route => route.routes ?? [route])
    .filter(route => !excludedRoutes?.includes(route.title))

  return (
    <footer className={cn('w-full', className)}>
      <div className={cn('w-full mx-auto', maxWidth)}>
        <div className="flex flex-col flex-wrap items-start justify-between sm:flex-row gap-8">
          <div className="flex items-center gap-2 min-w-[125px]">
            <Image src="/shared/ethena.svg" alt="Ethena" height={32} width={32} className="h-8 w-8 min-h-8 min-w-8" />
            <Image src="/shared/ethena-text.svg" alt="Ethena Logo" width={80} height={20} className="w-auto" />
          </div>
          {flattenedRoutes?.length > 0 && (
            <div className="grid sm:grid-flow-col sm:grid-rows-3 gap-4 sm:grid-cols-[repeat(3,120px)] md:grid-cols-[repeat(auto,160px)]">
              {flattenedRoutes?.map(route => (
                <Link
                  href={route.route}
                  key={route.title}
                  className="text-sm font-normal text-text-secondary hover:text-text-accent transition-all duration-300"
                >
                  {route.title}
                </Link>
              ))}
            </div>
          )}
          <div className="flex items-center justify-center gap-6 flex-wrap">
            {socials.map(({ alt, href, icon }) => (
              <Link href={href} target="_blank" key={alt} className="group">
                <Image
                  src={icon}
                  width={24}
                  height={24}
                  alt={alt}
                  className="h-6 w-6 group-hover:brightness-90 group-hover:saturate-200 transition-all duration-300"
                />
              </Link>
            ))}
            <div className="flex items-center gap-2">
              {handleReset && (
                <button className="text-[#808080] h-5 w-5" title="Reset Wallets" onClick={handleReset}>
                  <GrPowerReset className="h-5 w-5" />
                </button>
              )}
              {showBugReport && <BugReportButton />}
              {showGeneralEnquiries && (
                <Link href={GENERAL_ENQUIRIES_URL} target="_blank" rel="noreferrer">
                  <RiQuestionLine className="h-5 w-5 text-[#808080]" />
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
